import { useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { difficultiesLabels } from 'app/frontend/types/custom/Difficulty'
import RaidPlan from 'app/frontend/types/custom/RaidPlan'
import { useTeam } from 'app/frontend/base/config/context/team'
import { usePageInfo, useTeamRaidPlans } from 'app/frontend/base/config/queries'
import RaidPlanCandidatePicker from './RaidPlanCandidatePicker'
import { CopyIcon } from 'lucide-react'

type CopyFromPlanPopoverProps = {
  currentPlan: RaidPlan
  buttonLabel: string
  emptyLabel: string
  onSelectPlan: (plan: RaidPlan) => void
}

const CopyFromPlanPopover = ({
  currentPlan,
  buttonLabel,
  emptyLabel,
  onSelectPlan,
}: CopyFromPlanPopoverProps) => {
  const { team } = useTeam()
  const {
    data: { currentSeason },
  } = usePageInfo()
  const [open, setOpen] = useState(false)
  const { data: raidPlans } = useTeamRaidPlans(team.id, currentSeason?.id, open)

  const candidatePlans = useMemo(() => {
    return (raidPlans ?? [])
      .filter(
        (plan) =>
          plan.encounterId === currentPlan.encounterId &&
          plan.id !== currentPlan.id,
      )
      .sort((left, right) => {
        if (left.difficulty !== right.difficulty) {
          if (left.difficulty === currentPlan.difficulty) return -1
          if (right.difficulty === currentPlan.difficulty) return 1
          return difficultiesLabels[left.difficulty].localeCompare(
            difficultiesLabels[right.difficulty],
          )
        }
        return left.name.localeCompare(right.name)
      })
  }, [
    currentPlan.difficulty,
    currentPlan.encounterId,
    currentPlan.id,
    raidPlans,
  ])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          className='h-32 w-52 flex flex-col gap-3 items-center justify-center'
          variant='outline'
        >
          <CopyIcon className='w-8 h-8' />
          {buttonLabel}
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-fit max-w-[90vw]'>
        <div className='space-y-2'>
          <RaidPlanCandidatePicker
            plans={candidatePlans}
            warningDifficulty={currentPlan.difficulty}
            teamPath={team.path}
            onSelect={(plan) => {
              onSelectPlan(plan)
              setOpen(false)
            }}
            emptyLabel={emptyLabel}
          />
        </div>
      </PopoverContent>
    </Popover>
  )
}

export default CopyFromPlanPopover
